import "bootstrap/dist/css/bootstrap.css";
import "font-awesome/css/font-awesome.css";
import React, { Fragment } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { unstable_HistoryRouter as HistoryRouter } from "react-router-dom";
import { history } from "./utils/history";
import configureStore from "./store/configureStore";
import App from "./App";
import registerServiceWorker from "./registerServiceWorker";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import "./components/shared/global.css";
import "./components/shared/formiojs/formio.full.css";
import "./components/shared/formiojs/formio-overrides.css";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
// @mui/styles (legacy makeStyles) uses its own theming context, separate from
// @mui/material's ThemeProvider, so the app is wrapped in both with one shared
// theme. This keeps the custom theme keys (theme.button, theme.centeredForm, ...)
// resolving inside the 28 makeStyles call sites until they migrate to tss-react.
import { ThemeProvider as StylesThemeProvider } from "@mui/styles";

// ResizeObserver fires a benign "loop completed with undelivered notifications" (a.k.a. "loop limit
// exceeded") warning when a resize callback (MUI / Form.io layout) schedules another resize. It
// breaks nothing, but CRA's dev error overlay surfaces it as an "uncaught runtime error". Swallow
// just that message before the overlay's window 'error' handler sees it.
const IGNORED_RESIZE_OBSERVER_ERROR =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
window.addEventListener("error", (event) => {
  if (event.message && IGNORED_RESIZE_OBSERVER_ERROR.test(event.message)) {
    event.stopImmediatePropagation();
  }
});

// Get the application-wide store instance, prepopulating with state from the server where available.
const initialState = window.initialReduxState;
const store = configureStore(initialState);
const rootElement = document.getElementById("root");

const theme = createTheme({
  // MedSleep brand palette, sampled from the logo: navy "Med" + slate "Sleep".
  palette: {
    primary: {
      main: "#1B3A5F",
      light: "#46607F",
      dark: "#0F2435",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#7E9AC0",
      light: "#A7BBD6",
      dark: "#54708F",
      contrastText: "#ffffff",
    },
  },
  typography: {
    h3: { fontSize: "2.25rem", fontWeight: 600 },
    h4: { fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  // App-wide defaults so layout width and inputs stay consistent everywhere.
  components: {
    MuiContainer: { defaultProps: { maxWidth: "lg" } },
    MuiTextField: { defaultProps: { variant: "outlined", size: "small" } },
    // Match Selects/FormControls to the small outlined TextFields so every input is the same height.
    MuiFormControl: { defaultProps: { size: "small", variant: "outlined" } },
    MuiSelect: { defaultProps: { size: "small", variant: "outlined" } },
    MuiButton: { defaultProps: { disableElevation: true } },
  },
  backArrowOutter: {
    marginTop: "1rem",
    marginBottom: "1rem",
  },
  darkArrow: {
    color: "black",
    textDecoration: "none",
    "&:hover": {
      cursor: "pointer",
    },
  },
  hover: {
    "&:hover": {
      cursor: "pointer",
      color: "#3f51b5",
    },
  },
  button: {
    width: "100%",
    marginTop: "0.5rem",
    marginBottom: "1rem",
  },
  form: {
    width: "100%",
  },
  select: {
    marginTop: "1rem",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
  },
  chip: {
    margin: 2,
  },
  multiSelectEmpty: {
    width: "100%",
    marginTop: "1rem",
  },
  multiSelect: {
    width: "100%",
  },
  checkbox: {
    marginTop: "1rem",
  },
  paper: {
    display: "flex",
    flexWrap: "wrap",
    padding: "1rem",
    width: "100%",
    marginTop: "1rem",
  },
  indent: {
    marginLeft: "1rem",
  },
  radioGroup: {
    width: "auto",
    height: "auto",
    display: "flex",
    flexDirection: "row",
  },
  expansionPanel: {
    display: "inherit",
  },
  navLogo: {
    width: "150px",
  },
  exportLogo: {
    width: "200px",
  },
  linearProgress: {
    marginTop: "1rem",
    marginBottom: "1rem",
  },
  centeredForm: {
    // Full width on mobile; capped and centered on larger screens.
    width: "100%",
    maxWidth: "450px",
    minWidth: 0,
  },
  logo: {
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
    width: "75%",
    minWidth: "150px",
    marginTop: "1rem",
  },
});

const root = createRoot(rootElement);
root.render(
  <Provider store={store}>
    <HistoryRouter history={history}>
      <Fragment>
        <ToastContainer autoClose={5000} hideProgressBar={true} />
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <StylesThemeProvider theme={theme}>
              <App />
            </StylesThemeProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </Fragment>
    </HistoryRouter>
  </Provider>
);

registerServiceWorker();
